import { useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Users, CalendarDays, Stethoscope, BellRing, Receipt, Package, Truck,
  ShoppingCart, UserCog, HeartHandshake, UserPlus, BarChart3, Wrench, Building2, Store,
  Brain, Settings, LifeBuoy, Search, Bell, ChevronDown, LogOut, Plus, Menu, X, Headset, Inbox,
} from "lucide-react";
import api from "./api";
import Dashboard from "./pages/Dashboard";
import Leads from "./pages/Leads";
import Appointments from "./pages/Appointments";
import Patients from "./pages/Patients";
import Treatments from "./pages/Treatments";
import Billing from "./pages/Billing";
import Inventory from "./pages/Inventory";
import Staff from "./pages/Staff";
import SettingsPage from "./pages/Settings";
import Requests from "./pages/Requests";
import MultiClinic from "./pages/MultiClinic";
import Followups from "./pages/Followups";
import Reports from "./pages/Reports";
import Vendors from "./pages/Vendors";
import Procurement from "./pages/Procurement";
import { ListModule, StaticModule } from "./pages/ModulePage";

export const MENU = [
  { path: "", label: "Dashboard", icon: LayoutDashboard, end: true },
  { path: "patients", label: "Patients", icon: Users },
  { path: "appointments", label: "Appointments", icon: CalendarDays },
  { path: "treatments", label: "Treatments", icon: Stethoscope },
  { path: "followups", label: "Follow-ups", icon: BellRing },
  { path: "billing", label: "Billing & Finance", icon: Receipt },
  { path: "inventory", label: "Inventory", icon: Package },
  { path: "vendors", label: "Vendors", icon: Truck },
  { path: "procurement", label: "Procurement", icon: ShoppingCart },
  { path: "staff", label: "Staff Management", icon: UserCog },
  { path: "requests", label: "Requests", icon: Inbox, superOnly: true },
  { path: "crm", label: "CRM", icon: HeartHandshake },
  { path: "leads", label: "Leads", icon: UserPlus },
  { path: "reports", label: "Reports & Analytics", icon: BarChart3 },
  { path: "equipment", label: "Equipment", icon: Wrench },
  { path: "multiclinic", label: "Multi-Clinic", icon: Building2 },
  { path: "store", label: "Dyntra Store", icon: Store },
  { path: "ai-insights", label: "AI Insights", icon: Brain },
  { path: "settings", label: "Settings", icon: Settings },
  { path: "help", label: "Help & Support", icon: LifeBuoy },
];

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good Morning" : h < 17 ? "Good Afternoon" : "Good Evening";
}

export default function ConnectApp() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [mobileNav, setMobileNav] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [activities, setActivities] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [viewClinic, setViewClinic] = useState({
    id: localStorage.getItem("dyntra_connect_clinic_id"),
    name: localStorage.getItem("dyntra_connect_clinic"),
  });
  const searchRef = useRef(null);

  useEffect(() => {
    const sync = () => setViewClinic({
      id: localStorage.getItem("dyntra_connect_clinic_id"),
      name: localStorage.getItem("dyntra_connect_clinic"),
    });
    window.addEventListener("clinic-changed", sync);
    return () => window.removeEventListener("clinic-changed", sync);
  }, []);

  const goMainDashboard = () => {
    localStorage.setItem("dyntra_connect_clinic_id", "noida-75");
    localStorage.setItem("dyntra_connect_clinic", "Dyntra Clinics by Dr. Ankita, Noida");
    window.dispatchEvent(new Event("clinic-changed"));
    nav("/connect/app");
  };

  const viewingOtherClinic = user?.level === "super_admin" && viewClinic.id && viewClinic.id !== "noida-75";

  useEffect(() => {
    if (!localStorage.getItem("dyntra_connect_token")) { nav("/connect", { replace: true }); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {
      localStorage.removeItem("dyntra_connect_token");
      nav("/connect", { replace: true });
    });
    api.get("/connect/activities").then((r) => setActivities(r.data)).catch(() => {});
  }, [nav]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (q.trim().length >= 2) {
        api.get(`/connect/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)).catch(() => {});
      } else setResults(null);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const logout = () => {
    localStorage.removeItem("dyntra_connect_token");
    localStorage.removeItem("dyntra_connect_user");
    nav("/connect", { replace: true });
  };

  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const firstName = user?.name?.split(" ")[0] + " " + (user?.name?.split(" ")[1]?.[0] ? user.name.split(" ").slice(1).join(" ") : "");

  const sidebar = (
    <div className="flex h-full flex-col bg-pine-deep text-ivory">
      <Link to="/connect/app" className="flex items-center gap-3 px-5 pt-6 pb-5 border-b border-ivory/10">
        <img src="/dyntra-logo.jpg" alt="Dyntra" className="h-11 w-11 rounded-full object-cover ring-2 ring-gold/60" />
        <div>
          <p className="font-display text-lg font-semibold leading-tight">Dyntra <span className="text-teal-brand italic">Connect</span></p>
          <p className="text-[9px] uppercase tracking-[0.22em] text-gold font-bold">Dental Clinic Management</p>
        </div>
      </Link>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5" data-lenis-prevent>
        {MENU.filter((m) => !m.superOnly || (JSON.parse(localStorage.getItem("dyntra_connect_user") || "{}").level === "super_admin")).map((m) => (
          <NavLink
            key={m.path || "dash"}
            to={m.path ? `/connect/app/${m.path}` : "/connect/app"}
            end={m.end}
            data-testid={`connect-menu-${m.path || "dashboard"}`}
            onClick={() => setMobileNav(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[13px] font-semibold transition-colors duration-200 ${
                isActive ? "bg-teal-brand/90 text-white shadow-lift" : "text-ivory/65 hover:bg-white/5 hover:text-ivory"
              }`
            }
          >
            <m.icon className="h-4 w-4 shrink-0" strokeWidth={1.9} />
            {m.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-ivory/10">
        <div className="rounded-2xl bg-white/5 p-4">
          <p className="flex items-center gap-2 text-xs font-bold text-ivory/85"><Headset className="h-4 w-4 text-gold" /> Need Help?</p>
          <a href="https://wa.me/919801910117" target="_blank" rel="noopener noreferrer" className="mt-1.5 block text-xs text-teal-brand font-semibold hover:text-gold transition-colors">Chat with us</a>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="connect-app" className="h-screen flex bg-[#F4F6F4] overflow-hidden">
      <aside className="hidden lg:block w-64 shrink-0">{sidebar}</aside>
      {mobileNav && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-72">{sidebar}</div>
          <button aria-label="Close menu" className="flex-1 bg-pine-deep/60 backdrop-blur-sm" onClick={() => setMobileNav(false)}>
            <X className="h-6 w-6 text-ivory m-5" />
          </button>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="shrink-0 bg-white/85 backdrop-blur-xl border-b border-pine/8 px-4 md:px-8 py-4">
          <div className="flex items-center gap-3 md:gap-5">
            <button data-testid="connect-mobile-menu" className="lg:hidden rounded-xl p-2 border border-pine/10 text-pine-deep" onClick={() => setMobileNav(true)} aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="font-display text-lg md:text-2xl font-semibold text-pine-deep truncate">
                {greeting()}, {user ? user.name.split(" ").slice(0, 2).join(" ") : "Doctor"}
              </h1>
              <p className="hidden sm:block text-xs text-slate-500">Here's what's happening at your clinic today.</p>
            </div>

            <div className="relative ml-auto hidden md:block w-72 xl:w-96" ref={searchRef}>
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-pine/40" />
              <input
                data-testid="connect-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search patient, appointment, invoice..."
                className="h-10 w-full rounded-full border border-pine/12 bg-[#F4F6F4] pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-pine"
              />
              {results && (
                <div data-testid="connect-search-results" className="absolute top-12 inset-x-0 rounded-2xl bg-white border border-pine/10 shadow-grand p-3 z-40 max-h-80 overflow-y-auto" data-lenis-prevent>
                  {["patients", "appointments", "invoices"].map((k) =>
                    results[k].length > 0 && (
                      <div key={k} className="mb-2">
                        <p className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold text-slate-400">{k}</p>
                        {results[k].map((r, i) => (
                          <button
                            key={r.id || i}
                            onClick={() => { setResults(null); setQ(""); nav(`/connect/app/${k === "invoices" ? "billing" : k}`); }}
                            className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-cream text-sm font-medium text-pine-deep"
                          >
                            {r.name || r.patient || r.number} <span className="text-xs text-slate-400">{r.service || r.treatment || ""}</span>
                          </button>
                        ))}
                      </div>
                    )
                  )}
                  {!results.patients.length && !results.appointments.length && !results.invoices.length && (
                    <p className="px-2 py-2 text-sm text-slate-400">No matches found.</p>
                  )}
                </div>
              )}
            </div>

            <button data-testid="connect-quick-add" onClick={() => nav("/connect/app/patients")} className="hidden sm:flex h-10 w-10 items-center justify-center rounded-full bg-teal-brand text-white shadow-lift hover:bg-pine transition-colors" aria-label="Add patient">
              <Plus className="h-5 w-5" />
            </button>

            <div className="relative">
              <button data-testid="connect-bell" onClick={() => { setBellOpen(!bellOpen); setProfileOpen(false); }} className="relative h-10 w-10 flex items-center justify-center rounded-full border border-pine/12 text-pine-deep hover:bg-cream transition-colors" aria-label="Notifications">
                <Bell className="h-4.5 w-4.5 h-5 w-5" />
                {activities.length > 0 && <span className="absolute -top-1 -right-1 h-4.5 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{activities.length}</span>}
              </button>
              {bellOpen && (
                <div data-testid="connect-notifications" className="absolute right-0 top-12 w-80 rounded-2xl bg-white border border-pine/10 shadow-grand p-3 z-40">
                  <p className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold text-slate-400">Recent activity</p>
                  {activities.slice(0, 6).map((a) => (
                    <div key={a.id} className="flex items-start justify-between gap-3 px-2 py-2 rounded-lg hover:bg-cream">
                      <p className="text-[13px] font-medium text-pine-deep">{a.text}</p>
                      <span className="text-[10px] text-slate-400 shrink-0">{a.time}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <button data-testid="connect-profile" onClick={() => { setProfileOpen(!profileOpen); setBellOpen(false); }} className="flex items-center gap-2.5 rounded-full border border-pine/12 pl-1 pr-2.5 py-1 hover:bg-cream transition-colors">
                <img src="/dr-ankita-pandey.png" alt="" className="h-8 w-8 rounded-full object-cover object-[center_15%] ring-1 ring-gold/50" />
                <span className="hidden md:block text-left leading-tight">
                  <span className="block text-xs font-bold text-pine-deep">{user?.name || "..."}</span>
                  <span className="block text-[10px] text-slate-500">{user?.role || ""}</span>
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-pine/50" />
              </button>
              {profileOpen && (
                <div data-testid="connect-profile-menu" className="absolute right-0 top-12 w-56 rounded-2xl bg-white border border-pine/10 shadow-grand p-2 z-40">
                  <p className="px-3 py-2 text-xs text-slate-500">{user?.email}</p>
                  <p className="px-3 pb-2 text-xs font-semibold text-teal-brand">{user?.clinic || "Dyntra Clinics - Sector 75, Noida"}</p>
                  <Link to="/" className="block px-3 py-2 rounded-lg text-sm font-semibold text-pine-deep hover:bg-cream">View Website</Link>
                  <button data-testid="connect-logout" onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50">
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                </div>
              )}
            </div>

            <span className="hidden xl:inline-flex items-center gap-2 rounded-full border border-pine/12 px-4 py-2 text-xs font-semibold text-pine-deep">
              <CalendarDays className="h-3.5 w-3.5 text-gold" /> {today}
            </span>
            <span className="hidden 2xl:inline-flex items-center gap-2 rounded-full bg-gold-soft/60 px-4 py-2 text-xs font-bold text-pine-deep truncate max-w-56">
              {localStorage.getItem("dyntra_connect_clinic") || user?.clinic || "Dyntra Clinics - Sector 75, Noida"}
            </span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-8" data-lenis-prevent>
          {viewingOtherClinic && (
            <div data-testid="clinic-context-banner" className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl bg-gold-soft/60 border border-gold/40 px-5 py-3.5">
              <Building2 className="h-4 w-4 text-pine-deep shrink-0" />
              <p className="text-sm font-bold text-pine-deep flex-1">
                Viewing dashboard: {viewClinic.name}
              </p>
              <button
                data-testid="goto-main-dashboard"
                onClick={goMainDashboard}
                className="inline-flex items-center gap-2 rounded-full bg-pine-deep px-5 py-2 text-xs font-bold text-gold hover:bg-pine transition-colors"
              >
                <LayoutDashboard className="h-3.5 w-3.5" /> Go to Main Dashboard
              </button>
            </div>
          )}
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="leads" element={<Leads />} />
            <Route path="appointments" element={<Appointments />} />
            <Route path="patients" element={<Patients />} />
            <Route path="treatments" element={<Treatments />} />
            <Route path="billing" element={<Billing />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="followups" element={<Followups />} />
            <Route path="vendors" element={<Vendors />} />
            <Route path="procurement" element={<Procurement />} />
            <Route path="staff" element={<Staff />} />
            <Route path="requests" element={<Requests />} />
            <Route path="equipment" element={<ListModule module="equipment" title="Equipment" />} />
            <Route path="crm" element={<StaticModule title="CRM" />} />
            <Route path="reports" element={<Reports />} />
            <Route path="multiclinic" element={<MultiClinic />} />
            <Route path="store" element={<StaticModule title="Dyntra Store" store />} />
            <Route path="ai-insights" element={<StaticModule title="AI Insights" />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="help" element={<StaticModule title="Help & Support" />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
